import { CurrencyPipe } from "@angular/common";
import { Component, EventEmitter, inject, Input, Output } from '@angular/core';
import { RouterLink } from "@angular/router";
import {
  NgbDropdown,
  NgbDropdownItem,
  NgbDropdownMenu,
  NgbDropdownToggle,
  NgbTooltip
} from "@ng-bootstrap/ng-bootstrap";
import {
  AgHolderNumberOfSharesComponent
} from "@shared/components/grid/ag-cells/common/ag-holder-number-of-shares/ag-holder-number-of-shares.component";
import {
  AgYesNoStatusComponent
} from "@shared/components/grid/ag-cells/common/ag-yes-no-status/ag-yes-no-status.component";
import {
  ListGridClientSideComponent
} from "@shared/components/grid/components/list-grid-client-side/list-grid-client-side.component";
import { ACNPipe } from "@shared/pipes/acnPipe";
import { AsPipe } from "@shared/pipes/asPipe";
import { DefaulValuePipe } from "@shared/pipes/defaultValuePipe";
import { SecurityApiService } from "@shared/services/security-api.service";
import { ColDef, ValueGetterParams } from 'ag-grid-enterprise';
import { ToastrService } from 'ngx-toastr';
import {
  CorporateHolderModel,
  IndividualHolderModel,
  SecurityRegistryRecord
} from '@models/shares/security-registry-record';
import { SecurityType } from "@models/shares/security-type";
import { AuthSignatoryComponent } from "@shared/components/shares/auth-signatory/auth-signatory.component";
import { BeneficialOwnerComponent } from "@shared/components/shares/beneficial-owner/beneficial-owner.component";
import { DividerComponent } from "@shared/components/commons/divider/divider.component";

@Component({
  selector: 'app-securityholder-profile',
  standalone: true,
  templateUrl: 'securityholder-profile.component.html',
  styleUrl: 'securityholder-profile.component.scss',
  imports: [
    DividerComponent,
    RouterLink,
    AsPipe,
    ACNPipe,
    DefaulValuePipe,
    AgYesNoStatusComponent,
    BeneficialOwnerComponent,
    AuthSignatoryComponent,
    NgbDropdown,
    NgbDropdownToggle,
    NgbDropdownItem,
    NgbDropdownMenu,
    NgbTooltip,
    ListGridClientSideComponent
  ]
})
export class SecurityholderProfileComponent {
  securityService = inject(SecurityApiService);
  toastr = inject(ToastrService);
  currencyPipe = inject(CurrencyPipe);

  @Input() showCompanyActions = false;
  @Input() securityholder!: SecurityRegistryRecord;

  @Input('pendingButtonState') set pendingButtonStateSetter(pendingButtonState: boolean) {
    this.pendingButtonState = pendingButtonState;
    this.colDefs = this.colDefs.map((colDef) => {
      if ('cellRendererParams' in colDef) {
        colDef.cellRendererParams['pendingButtonState'] = pendingButtonState;
      }

      return colDef;
    });
  }

  @Output() onDataSaved = new EventEmitter();
  @Output() profileViewChange = new EventEmitter<boolean>();
  @Output() changeShareholderPartyName = new EventEmitter<string[]>();
  @Output() changeShareholderAddress = new EventEmitter<string[]>();

  CorporateHolderModel = CorporateHolderModel;
  IndividualHolderModel = IndividualHolderModel;
  pendingButtonState = true;
  colDefs = this.getColDefs();

  onCompanyChangePartyName(): void {
    this.changeShareholderPartyName.emit(this.shareholdersRelationshipIds);
  }

  onChangeOfficerAddress(): void {
    this.changeShareholderAddress.emit(this.shareholdersRelationshipIds);
  }

  get shareholdersRelationshipIds(): string[] {
    return this.securityholder.holders.map((holder) => holder.relationshipId);
  }

  getColDefs(): ColDef[] {
    return [
      {
        headerName: 'Security Type',
        valueGetter: (params: ValueGetterParams<SecurityRegistryRecord>) => params.data!.holdings?.[0]?.securityType?.identifier ?? '-',
        flex: 2,
        sort: 'asc',
        minWidth: 100
      },
      {
        headerName: '# of Shares',
        field: 'number',
        flex: 1,
        cellRenderer: AgHolderNumberOfSharesComponent,
        cellRendererParams: { pendingButtonState: true },
        minWidth: 100,
        cellClass: 'h-100 d-flex align-items-center'
      },
      {
        headerName: 'Paid Amount',
        valueGetter: (params: ValueGetterParams<SecurityRegistryRecord>) => SecurityType.getMoneyLabel(params.data!.holdings?.[0]?.paid, this.currencyPipe),
        flex: 1,
        minWidth: 100
      },
      {
        headerName: 'Unpaid Amount',
        valueGetter: (params: ValueGetterParams<SecurityRegistryRecord>) => SecurityType.getMoneyLabel(params.data!.holdings?.[0]?.unpaid, this.currencyPipe),
        flex: 1,
        minWidth: 100
      },
      {
        headerName: 'Fully Paid',
        field: 'fullyPaid',
        flex: 1,
        valueGetter: (params: ValueGetterParams<SecurityRegistryRecord>) => params.data!.fullyPaid,
        cellRenderer: AgYesNoStatusComponent,
        minWidth: 50
      },
    ]
      .map((colDef) => ({ ...colDef, suppressHeaderMenuButton: true } as ColDef));
  }
}
